/**
 * WorkItemDrawer — the intake drawer matched to the prototype (PR4-fidelity +
 * PR4b): "<stage> · step X of 7" + client name + a "Next:" action, the referral
 * source (a real link to its Directory page), assigned-to, opened/due, the
 * REAL intake contact (PR4b — captured in Peace OS, distinct from the CareTree
 * client record which remains the master), where the client is, with a link
 * to the map (LocationField, under Unit), the editable intake-tracker fields,
 * the Close/Reopen action, the intake checklist, and the activity rail. A
 * client-Directory link stays OMITTED (no client page). Write controls gate on
 * canManageWorkItems (the server re-checks).
 *
 * ============================================================
 * DESIGN PASS 2026-07-25 (soak r2) — the drawer gets a SPINE.
 * ============================================================
 * Founder: "it's getting long, beige and with chaotic and not quite aligned
 * formatting." All three were true, and they were the record page's
 * wall-of-beige problem (adjudicated R11-R14) arriving in a 440px column.
 * The fix speaks the record page's OWN language (drawerLayout carries the
 * anatomy; docs/reference/design-language.md is the law it follows):
 *
 *   LENGTH — the drawer is now: pinned header · scrolling body · pinned
 *   footer. The body's order is READ FIRST, then work, then history:
 *   status band → At a glance → Intake details (collapsed) → checklist
 *   → activity (truncated). The two longest passengers, the 13-field
 *   edit form and the unbounded event stream, no longer sit in the read
 *   path by default.
 *
 *   ALIGNMENT — ONE grid. Summary rows, collaborators, the reversibility
 *   actions and every intake edit field now share drawerLayout's label
 *   column. Previously: five left edges (a 122px summary label column, a
 *   2-up form with labels above controls, an ungridded reversibility row,
 *   a -ml-2 lifecycle row and the checklist's own rails).
 *
 *   BEIGE — sections are cards on the sanctioned surface ladder (paper for
 *   the work, the DL4 sage-50 tint zone for standing setup), on the cream
 *   ground, so the eye can find the section it wants. No new colors.
 *
 * The lifecycle cluster (Close intake / Delete / Archive / Mark complete)
 * moved OUT of mid-body into the PINNED FOOTER (#616 punch item 1). The
 * #615 stage-advance bar stays exactly where it is, at the end of the
 * current stage's checklist section — that placement is law.
 */

import { Button } from '@/components/primitives/Button';
import { useState, type ReactNode } from 'react';
import { useTenant } from '@/lib/auth';
import { displayValue } from '@/lib/valueCase';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { readTaskCategories, taskCategoryLabel } from '@/lib/taskCategories';
import { EMPTY_PROCESS_CARD_FIELDS, readProcessCardFields } from '@/lib/processCardFields';
import { useWorkspaceProcesses } from '@/lib/workspaceProcesses';
import {
  canManageWorkItems,
  canManageOwnTasks,
  canViewAnyClientRoster,
  canManageClientIdentity,
  canReadClinicalRecord,
  canWriteClinicalNotes, canModerateWorkItemNotes } from '@/lib/capabilities';
import { Link } from 'react-router-dom';
import { CLIENT_BUBBLE_CLASS } from '@/components/workspace/ClientBubble';
import { ClientLookupField } from '@/components/aiTools/ClientLookupField';
import {
  useWorkItemDetail, useToggleChecklistItem, useSetChecklistNote, useSetChecklistLink, useSetChecklistDoneDate, useSetChecklistValue, useAddComment, usePipelineStages,
  usePatchWorkItem, useIntakeOptions, useCloseIntake, useReopenIntake, useMoveStage, useUnassignCm, useReassignCm,
  useAssignOptions, useWorkspaceMembers, useDeleteTask, useDeleteIntake, useDeleteProcessItem, usePauseRecurringSeries,
  useRecurringSeries, useUpdateRecurringSeries, useAttachRecurrence, useCompleteRecurringTask,
  useEditEvent,
  useDeleteEvent,
  useSetEventEnteredOn,
} from '@/lib/workspace';
// 0155 Part B — the client's EXISTING soft-archive (reused, not rebuilt): the
// roster lookup resolves whether a converted intake's linked client is
// already archived (so the drawer offers Restore instead of Archive), and
// the mutation hook fires the SAME endpoint the Client Record page uses.
import { useClientsList, useSetClientArchived } from '@/lib/clientsApi';
import { Drawer } from '@/components/primitives/Drawer';
import { CmBlock } from './drawer/CmBlock';
import { ActivityBlock } from './drawer/ActivityBlock';
import { AttachmentsBlock } from './drawer/AttachmentsBlock';
import { ClientDocumentsBlock } from './drawer/ClientDocumentsBlock';
import { ReferralBlock } from './drawer/ReferralBlock';
import { RecurrenceBlock } from './drawer/RecurrenceBlock';
import { LifecycleActions, LifecycleStatusBanner } from './drawer/CloseCompleteBlock';
import { IntakeDetailsForm } from './drawer/IntakeDetailsForm';
import { DrawerSection, DrawerRow, DrawerRowList } from './drawer/drawerLayout';
import { AssignReversibilityBlock } from './drawer/AssignReversibilityBlock';
import { LocationField } from './drawer/LocationField';
import { useAdvanceFlow } from './drawer/useAdvanceFlow';
import { DrawerHeaderBlock } from './drawer/DrawerHeaderBlock';
import { StageChecklist, requiredLeafProgress } from './StageChecklist';
import { MoveStageDialog } from './MoveStageDialog';
import { HandoffDialog } from './HandoffDialog';
import { ConfirmDialog } from './ConfirmDialog';
import { CollaboratorsField } from './CollaboratorsField';
import { Assignee } from './Assignee';
import { TextAction } from './TextAction';
import { SuggestedCareManagers } from './SuggestedCareManagers';
import { ASSIGNED_STAGE_KEY, READY_FOR_ASSIGNMENT_STAGE_KEY, splitProspectName } from '@/lib/workspaceStageMove';
import { ArrowUpRight } from 'lucide-react';
import { avatarClasses, shortDate, moveBlockedMessage, unitDotColor, isOverdue } from './workspaceFormat';
import { useSupportContact } from '@/hooks/useSupportContact';
import { ErrorBanner } from '@/components/ErrorBanner';
import { SkeletonRow } from '@/components/Skeleton';
import type { UpdateWorkItemInput } from '@/lib/api';
import { WORKSPACE_DRAWER_WIDTH_KEY } from '@/lib/keptAcrossSignOut';
import { SelectField } from '@/components/primitives/SelectField';
import { DateInput } from '@/components/primitives/DateInput';

// Slice 3b-2 (#5): resizable width persisted per browser — the mechanics moved
// into the Drawer primitive (UX-11 PR-1); the key stays here so the workspace
// drawer keeps every user's stored width.
const DRAWER_WIDTH_KEY = WORKSPACE_DRAWER_WIDTH_KEY;

export interface WorkItemDrawerProps {
  itemId: string;
  onClose: () => void;
}

/**
 * The drawer's summary rows. The local hand-rolled Field (a 122px label
 * column and a per-row dashed bottom border) retired into drawerLayout's
 * DrawerRow, so every label:value pair in the drawer — read and edit
 * alike — lands on ONE grid, and the dividers come from the row list.
 */
const Field = DrawerRow;

export function WorkItemDrawer({ itemId, onClose }: WorkItemDrawerProps): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const contact = useSupportContact();
  const canManage = canManageWorkItems(whoami);
  // The intake Documents section rides the CLINICAL capabilities — the same
  // gates the client record's Documents tab and its handlers use — never the
  // drawer's own view_workspace/manage_work_items. See ClientDocumentsBlock's
  // header: the read delta against view_workspace is empty, so this widens
  // nothing, and gating writes here (rather than on manage_work_items) is what
  // keeps board_observer read-only by rule instead of by coincidence.
  const canReadDocuments = canReadClinicalRecord(whoami);
  const canFileDocuments = canWriteClinicalNotes(whoami);
  const detailQuery = useWorkItemDetail(tenantSlug, itemId);
  // LP-2: an own-tasks holder (manage_own_tasks, no manage_work_items) edits
  // and completes a TASK assigned to them, and removes one they also made;
  // never the hand-off, stage, collaborator, attachment or recurrence
  // controls, which stay on canManage. The server decides on the row.
  const ownTask =
    !canManage &&
    canManageOwnTasks(whoami) &&
    detailQuery.data?.kind === 'task' &&
    detailQuery.data.assignee_user_id === (whoami?.user_id ?? null);
  const canEdit = canManage || ownTask;
  const ownTasksOnly = !canManage && canManageOwnTasks(whoami);
  const canRemoveTask = canManage || (ownTask && detailQuery.data?.created_by_user_id === whoami?.user_id);
  // Load the card's OWN process's stages (0154): a process_item lives on a
  // non-intake board, so its stage/checklist grouping needs that board's stages
  // — not the intake set. process_id is null pre-load + for tasks (→ intake
  // stages, which tasks don't use anyway).
  const { stages } = usePipelineStages(tenantSlug, { processId: detailQuery.data?.process_id ?? undefined });
  const toggleItem = useToggleChecklistItem(tenantSlug);
  const setNote = useSetChecklistNote(tenantSlug);
  const setLink = useSetChecklistLink(tenantSlug);
  const setDoneDate = useSetChecklistDoneDate(tenantSlug);
  const setValue = useSetChecklistValue(tenantSlug);
  const addComment = useAddComment(tenantSlug);
  const editEvent = useEditEvent(tenantSlug);
  const deleteEvent = useDeleteEvent(tenantSlug);
  const setEnteredOn = useSetEventEnteredOn(tenantSlug);
  const patchItem = usePatchWorkItem(tenantSlug);
  const completeRecurring = useCompleteRecurringTask(tenantSlug);
  const closeIntake = useCloseIntake(tenantSlug);
  const reopenIntake = useReopenIntake(tenantSlug);
  const deleteTask = useDeleteTask(tenantSlug);
  // 0155 Part A — delete an UNCONVERTED intake (pre-client-setup dedup).
  const deleteIntake = useDeleteIntake(tenantSlug);
  // 0160 — delete a process-board card (candidate cleanup).
  const deleteProcessItem = useDeleteProcessItem(tenantSlug);
  const pauseSeries = usePauseRecurringSeries(tenantSlug);
  // The rule behind a recurring task (its structured cadence/anchors) — prefills the
  // "Edit repeat" picker, which the WorkItemRow's summary string can't.
  const ruleQuery = useRecurringSeries(tenantSlug, detailQuery.data?.recurrence_id ?? null, { enabled: canEdit });
  const updateSeries = useUpdateRecurringSeries(tenantSlug);
  const attachRecurrence = useAttachRecurrence(tenantSlug);
  const moveStage = useMoveStage(tenantSlug);
  const unassignCm = useUnassignCm(tenantSlug);
  const reassignCm = useReassignCm(tenantSlug);
  // Loaded for all workspace viewers (not just managers): the read-time display
  // below falls back to defaults.referrer_type when referrer_type is null.
  const { lists, defaults, freeText } = useIntakeOptions(tenantSlug);
  // WS-7: the tenant's task-category vocabulary (config seam; platform default).
  const { data: tenantConfig } = useTenantConfig(tenantSlug, { enabled: tenantSlug !== '' });
  const categories = readTaskCategories(tenantConfig?.config);
  // Field pack v1: a process_item card's board-level field config, resolved
  // via its process's immutable KEY (the shared processes cache — one fetch
  // per session with the board page).
  const processesQ = useWorkspaceProcesses(tenantSlug, { enabled: detailQuery.data?.kind === 'process_item' });
  const processKey = processesQ.processes.find((p) => p.id === detailQuery.data?.process_id)?.key ?? null;
  const cardFields =
    detailQuery.data?.kind === 'process_item' && processKey
      ? readProcessCardFields(tenantConfig?.config, processKey)
      : EMPTY_PROCESS_CARD_FIELDS;
  // Slice 3b: units for the unit picker; members for the handoff pop-up (manager-only).
  const { units } = useAssignOptions(tenantSlug, { enabled: canManage });
  const { members } = useWorkspaceMembers(tenantSlug, { enabled: canManage });
  // 0155 Part B — archive/restore the CONVERTED intake's client (the existing
  // client soft-archive endpoint, reused). canArchiveClient gates BOTH the
  // affordance AND the roster lookup below — every role holding
  // manage_client_identity also holds view_all_clinical (the clients-list
  // gate), so a denied role fires neither request.
  const canArchiveClient = canManageClientIdentity(whoami);
  const { clients: archiveLookupClients } = useClientsList(tenantSlug, {
    includeArchived: true,
    enabled: canArchiveClient && !!detailQuery.data?.client_id,
  });
  const archiveClient = useSetClientArchived(tenantSlug);
  const [taskAction, setTaskAction] = useState<'delete' | 'stop' | null>(null);
  const [assigning, setAssigning] = useState(false);
  // Done · advance into Client Setup: the stage the form moves to, while open.
  const [advanceSetupTo, setAdvanceSetupTo] = useState<string | null>(null);
  // A pick from the checklist's Suggested block; the Assign dialog opens with it selected.
  const [assignInitialRosterId, setAssignInitialRosterId] = useState<string | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  // PR3: the process_item generic stage control's error (a gate refusal names
  // the unfinished item(s) via moveBlockedMessage — never the word "gate").
  const [processMoveError, setProcessMoveError] = useState<string | null>(null);
  // Soak 2026-07-25: the process-board checkpoint bar's error — rendered next
  // to the checklist (where the bar is), not up at the Stage field.
  const [processAdvanceError, setProcessAdvanceError] = useState<string | null>(null);
  const [handoffOpen, setHandoffOpen] = useState(false);
  // Brief 3 PR3: a generic styled confirm for the reversibility actions (move-back,
  // unassign); 0155 reuses it for the intake-delete + archive/restore-client confirms
  // (destructive controls the pepper vs primary confirm button — Delete intake only).
  const [confirmAction, setConfirmAction] = useState<{ title: string; body?: string; confirmLabel: string; run: () => void; destructive?: boolean } | null>(null);
  // Edit an intake field (PATCH). Closed-state fields go via close/reopen.
  const editField = (edits: UpdateWorkItemInput): void => {
    if (detail) patchItem.mutate({ id: detail.id, edits });
  };

  const detail = detailQuery.data;
  // 0155 Part B — the linked client's archived state (null = unknown/loading/
  // not applicable). The roster lookup only fires when detail.client_id is set
  // (see the enabled gate above), so this resolves once the intake has converted.
  const linkedClient = detail?.client_id ? archiveLookupClients.find((c) => c.id === detail.client_id) : undefined;
  const clientArchived = linkedClient ? !linkedClient.is_active : null;
  const currentStageKey = detail?.stage_id ? (stages.find((s) => s.id === detail.stage_id)?.key ?? null) : null;
  const stageLabel = detail?.stage_id ? (stages.find((s) => s.id === detail.stage_id)?.label ?? null) : null;
  // Slice 2: "Assign care manager" replaces the removed drag-to-Assigned column.
  // Shown for a Ready-for-Assignment intake with no CM yet; opens the Assign form
  // (assignee_roster_id → clients.primary_cm_roster_id). Server re-checks the gates.
  const canAssignCm = !!detail && canManage && detail.kind === 'intake'
    && currentStageKey === 'ready_for_assignment' && !detail.assignee_roster_id && !detail.closed_at;

  // Slice 3a + #4 cascade: header + "Next" reflect the CURRENT stage's REQUIRED LEAF
  // items (is_optional AND parent roll-up headers excluded — a parent is a roll-up,
  // not a counted/nagged peer).
  const stageLabelByKey = new Map(stages.map((s) => [s.key, s.label]));
  const currentStageItems = detail?.checklist.filter((c) => c.maps_to_stage_key === currentStageKey) ?? [];
  const currentStageParentIds = new Set<string>();
  for (const c of currentStageItems) if (c.parent_id) currentStageParentIds.add(c.parent_id);
  const stageProgress = requiredLeafProgress(currentStageItems);
  // Slice 3b — "Done · advance" checkpoint. Next stage = the next pipeline stage by
  // sort_order. Suppressed on Ready-for-Assignment (its advance is the Assign dialog).
  const orderedStages = [...stages].sort((a, b) => a.sort_order - b.sort_order);
  const curIdx = orderedStages.findIndex((s) => s.key === currentStageKey);
  const nextStage = curIdx >= 0 ? (orderedStages[curIdx + 1] ?? null) : null;
  const readyStage = orderedStages.find((s) => s.key === 'ready_for_assignment') ?? null;
  const showAdvance = !!detail && canManage && detail.kind === 'intake' && !detail.closed_at
    && currentStageKey !== 'ready_for_assignment' && !!nextStage;
  // Brief 3 Design B: Assigned is the final WORKED stage; its terminal advance
  // ("Done • Move to Completed" — StageChecklist sets that label itself via
  // isTerminalStage) CLOSES the case (close_reason='completed') instead of moving,
  // since there's no next stage.
  const showTerminalComplete = !!detail && canManage && detail.kind === 'intake'
    && !detail.closed_at && currentStageKey === ASSIGNED_STAGE_KEY;

  // Soak 2026-07-25 (team ask via founder): the process-board drawer gets the
  // SAME checkpoint bar as the intake drawer — advance the card to the next
  // lane from the drawer instead of dragging. Fires the plain moveStage the
  // board's drag path uses (same canManage gate; the server enforces the
  // forward gates and a refusal names the unfinished item(s)). A terminal
  // stage has no next lane, so no bar.
  const showProcessAdvance = !!detail && canManage && detail.kind === 'process_item'
    && !detail.closed_at && !!nextStage;
  function handleProcessAdvance(): void {
    if (!detail || !nextStage) return;
    setProcessAdvanceError(null);
    moveStage.mutate(
      { id: detail.id, input: { to_stage: nextStage.key } },
      { onError: (err) => setProcessAdvanceError(moveBlockedMessage(err, contact)) },
    );
  }

  // Slice 2 → soak 2026-07-25: "Assign care manager" IS Ready-for-Assignment's
  // stage advance, so it renders in the checklist's checkpoint slot (end of the
  // RfA section) like every other stage's bar. Opens the Assign dialog.
  const [assignSpiceFirst, setAssignSpiceFirst] = useState(false);
  const startAssign = (): void => { setAssignError(null); setAssignInitialRosterId(null); setAssignSpiceFirst(false); setAssigning(true); };
  // LP-3: the same dialog, opened from "Assign SPICE therapist".
  const startAssignSpice = (): void => { setAssignError(null); setAssignInitialRosterId(null); setAssignSpiceFirst(true); setAssigning(true); };
  const startAssignWith = (rosterId: string): void => { setAssignError(null); setAssignInitialRosterId(rosterId); setAssigning(true); };
  // The Suggested block, under the first Ready for Assignment checklist item
  // (where the unit lead decides who takes the case): a quiet link opens it,
  // and a name in it opens the Assign dialog with that person picked. It
  // shows for an intake card with a client (whose home address decides) or
  // a ZIP.
  const canSuggest = detail?.kind === 'intake' && canManage;
  const suggestZip = canSuggest ? (detail.prospect_zip?.trim() ?? '') : '';
  const suggestClientId = canSuggest ? (detail.client_id ?? null) : null;
  const suggestNode: ReactNode = suggestClientId !== null || suggestZip !== '' ? (
    <div className="ml-7 mb-1">
      <TextAction data-testid="drawer-suggested-toggle" onClick={() => setSuggestOpen((v) => !v)} aria-expanded={suggestOpen}>
        Suggested care managers
      </TextAction>
      {suggestOpen && (
        <div className="mt-1 mb-2">
          <SuggestedCareManagers
            zip={suggestZip}
            clientId={suggestClientId}
            selectedId={null}
            onSelect={(row) => startAssignWith(row.roster_id)}
            testIdPrefix="drawer-suggested"
          />
        </div>
      )}
    </div>
  ) : null;

  // Soak r2 — the client's prospect-folder URL, derived from the checklist
  // ALREADY in the detail payload (mirrors listWorkItems' folder_link_url rule,
  // #612: the FIRST allows_link item with a URL by sort_order; on the Peace
  // template that URL lives on a Client Setup parent item). Surfaced as a
  // distinguished link at the top of the Ready for Assignment checklist section
  // so unit leads doing assignment see it without hunting back through the
  // Client Setup group. Nothing renders when no URL was captured.
  const checklistFolderUrl: string | null = detail
    ? ([...detail.checklist]
        .sort((a, b) => a.sort_order - b.sort_order)
        .find((c) => c.allows_link && c.link_url !== null)?.link_url ?? null)
    : null;
  const folderLinkUrl: string | null = detail?.kind === 'intake' ? checklistFolderUrl : null;
  // A process card (talent, say) links to its folder from the summary: the
  // per-card checklist URL when captured, else the board's configured
  // folder under its configured label. Mirrors the card face.
  const processFolderUrl: string | null = detail?.kind === 'process_item'
    ? (checklistFolderUrl ?? cardFields.folder_link?.url ?? null)
    : null;
  const processFolderLabel = cardFields.folder_link?.label ?? 'Folder';
  // Navigation, so a link (new tab) — the outlined link-pill register (the
  // Directory "Website" pill, drawer scale), distinct from checklist item
  // text without reading as a primary button.
  const folderLinkNode = folderLinkUrl !== null ? (
    <a
      href={folderLinkUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="drawer-folder-link"
      className="mt-1.5 mb-2 inline-flex items-center gap-1.5 rounded-pill border border-sage-200 bg-paper px-3 py-1 text-[12.5px] font-semibold text-sage-800 no-underline shadow-sh1 hover:bg-sage-50 hover:shadow-sh2 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
    >
      Open Client Prospect Folder
      <ArrowUpRight size={13} aria-hidden="true" className="text-sage-400 flex-shrink-0" />
    </a>
  ) : null;
  // The geographic unit has ONE editor: the header select beside the stage
  // (DrawerHeaderBlock), which is always in view and can change the unit at
  // any stage. The Ready for Assignment section, where the unit lead
  // assigns the case, shows the unit as a fact with a Change door that
  // hands off to that select.
  const unitPickerNode = detail?.kind === 'intake' && canManage ? (
    <p className="mt-1 mb-1.5 flex items-center gap-2 text-[0.78125rem] text-sage-400" data-testid="drawer-unit-row">
      Unit
      <span className="min-w-0 [overflow-wrap:anywhere] text-ink" data-testid="drawer-unit-fact">{detail.unit_name ?? 'Not set'}</span>
      <Button
        variant="ghost"
        size="sm"
        className="shrink-0 !min-h-0 !px-1.5 !py-0 text-[0.78125rem]"
        data-testid="drawer-unit-change"
        onClick={() => {
          const select = document.querySelector<HTMLSelectElement>('[data-testid="drawer-header-unit-select"]');
          select?.focus();
        }}
      >
        {detail.unit_name ? 'Change' : 'Set'}
      </Button>
    </p>
  ) : null;
  const readyForAssignmentIntro = detail?.kind === 'intake' && (unitPickerNode !== null || folderLinkNode !== null)
    ? {
        stageKey: READY_FOR_ASSIGNMENT_STAGE_KEY,
        node: (
          <div data-testid="drawer-rfa-intro">
            {unitPickerNode}
            {folderLinkNode}
          </div>
        ),
      }
    : null;

  // Brief 3 PR3 — reversibility. From Assigned (the only stage with a CM): Reassign
  // (swap CM in place) or Unassign (drop CM → re-queue to Ready-for-Assignment).
  const isAssigned = currentStageKey === ASSIGNED_STAGE_KEY;
  const canReassignCm = !!detail && canManage && detail.kind === 'intake' && !detail.closed_at && isAssigned && !!detail.assignee_roster_id;
  const canUnassignCm = canReassignCm;
  // Move back: pick any EARLIER stage (not from new_prospect/first; not from Assigned,
  // which uses unassign). Side-effects persist (the client row + any CM stay).
  const priorStages = curIdx > 0 ? orderedStages.slice(0, curIdx) : [];
  const canMoveBack = !!detail && canManage && detail.kind === 'intake' && !detail.closed_at && !isAssigned && priorStages.length > 0;

  // Stage-advance / terminal-complete choreography (spans header, checklist,
  // and two confirm dialogs) — extracted to drawer/useAdvanceFlow (UX-11 PR-2).
  const {
    advanceError,
    setAdvanceError,
    confirmAdvance,
    setConfirmAdvance,
    doAdvance,
    handleAdvance,
    doComplete,
    handleTerminalComplete,
  } = useAdvanceFlow({
    detail,
    currentStageItems,
    currentStageParentIds,
    stageLabel,
    nextStage,
    readyStage,
    moveStage,
    closeIntake,
    onAdvanced: () => setHandoffOpen(true), // Block C: hand off to the next owner
    // Into Client Setup with no client yet: the Client Setup form, as the
    // board's drag opens it; its send keeps auto_complete_stage.
    onOpenClientSetup: (toStageKey) => setAdvanceSetupTo(toStageKey),
  });


  // The due date — settable by managers, read-only otherwise. Rendered FIRST for a
  // task (the most actionable field); an intake keeps it in its stage-driven order.
  // Field pack v1: a process card with a configured due-date label (e.g. a
  // hiring board's "Application date") renders under that label instead.
  const dueDateLabelText = (detail?.kind === 'process_item' && cardFields.due_date_label) || 'Due Date';
  const dueDateField = detail && (
    canEdit ? (
      <Field label={dueDateLabelText}>
        <DateInput
          defaultValue={detail.due_date ?? ''}
          aria-label={dueDateLabelText}
          data-testid="drawer-due-date"
          onChange={(e) => editField({ due_date: e.target.value || null })}
          size="compact"
        />
      </Field>
    ) : (
      detail.due_date && <Field label={dueDateLabelText}>{shortDate(detail.due_date)}</Field>
    )
  );

  // A process card's values: the full set when present, else the one
  // category (a legacy row carries category with no set).
  const processCategoryValues: string[] = detail?.categories ?? (detail?.category ? [detail.category] : []);

  // The day to touch this card next: its own row beside Due Date, for every
  // kind. Managers set/clear it; others read it (no row when unset).
  const nextActionField = detail && (
    canEdit ? (
      <Field label="Next action">
        <DateInput
          defaultValue={detail.next_action_date ?? ''}
          aria-label="Next action"
          data-testid="drawer-next-action"
          onChange={(e) => editField({ next_action_date: e.target.value || null })}
          size="compact" className="max-w-full"
        />
      </Field>
    ) : (
      detail.next_action_date && (
        <Field label="Next action">
          <span data-testid="drawer-next-action-readonly">{shortDate(detail.next_action_date)}</span>
        </Field>
      )
    )
  );

  return (
    <>
      <Drawer
        onClose={onClose}
        ariaLabel="Work item detail"
        testId="work-item-drawer"
        widthStorageKey={DRAWER_WIDTH_KEY}
        // #616 punch item 1: the lifecycle cluster is a REAL pinned footer
        // now — the exits live in one place and are always reachable,
        // instead of surfacing in the middle of the scroll between the
        // reversibility row and the intake form.
        footer={
          <>
            {detail && (
              <LifecycleActions
                detail={detail}
                canManage={canEdit}
                canRemoveTask={canRemoveTask}
                currentStageKey={currentStageKey}
                closeReasons={lists.close_reason}
                closeIntake={closeIntake}
                patchItem={patchItem}
                completeRecurring={completeRecurring}
                onTaskAction={setTaskAction}
                requestConfirm={setConfirmAction}
                deleteIntake={deleteIntake}
                deleteProcessItem={deleteProcessItem}
                onIntakeDeleted={onClose}
                canManageClientIdentity={canArchiveClient}
                clientArchived={clientArchived}
                archiveClient={archiveClient}
              />
            )}
          </>
        }
      >
        <DrawerHeaderBlock
          detail={detail}
          canManage={canEdit}
          onClose={onClose}
          stageLabel={stageLabel}
          stageProgress={stageProgress}
          showAdvance={showAdvance}
          nextStage={nextStage}
          onEdit={editField}
          units={units}
        />

        {/* The ONE scrolling region — the header above it and the lifecycle
            footer below it stay put. */}
        <div className="flex-1 min-h-0 overflow-auto px-5 py-4 space-y-3" data-testid="drawer-body">
          {detailQuery.isLoading && (<><SkeletonRow /><SkeletonRow /></>)}
          {detailQuery.isError && <ErrorBanner error={detailQuery.error} onRetry={() => detailQuery.refetch()} what="this card" />}

          {detail && (
            <>
              {/* The lifecycle STATE reads first: "Closed · no response"
                  changes how everything below it should be read. */}
              <LifecycleStatusBanner
                detail={detail}
                canManage={canEdit}
                reopenIntake={reopenIntake}
                patchItem={patchItem}
              />

              <DrawerSection title="At a glance" testId="drawer-summary">
                <DrawerRowList>
                {(detail.referral_org_resolved_name || detail.referral_individual_name || detail.referral_text) && (
                  <Field label="Referral source">
                    <ReferralBlock detail={detail} tenantSlug={tenantSlug} />
                  </Field>
                )}
                {/* Slice 3c: the owning CM gets a rich block — photo + name +
                    "Copy intro email" (the whole assembled family CM-intro
                    email, same as the People tab). The full bio USED to print
                    here and swamped the glance; it is now carried only as a
                    paragraph of the copied email (founder, 2026-07-26). A
                    non-CM desk owner (assignee_user only) keeps the plain
                    "Owner" line.
                    Soak r2 (mirrors the #612 card row): in the assignment cluster
                    (Ready for Assignment + Assigned) the row is ALWAYS visible —
                    "Unassigned" + the dashed ? avatar when no roster CM (never the
                    desk owner's name; assignee_user is not the care manager). In
                    the Assigned stage an unassigned row carries an inline Assign
                    action — the #615 assign bar renders only in Ready for
                    Assignment, so a CM-less Assigned case had no affordance. */}
                {detail.assignee_roster_id ? (
                  <Field label="Care manager">
                    <CmBlock
                      tenantSlug={tenantSlug}
                      rosterId={detail.assignee_roster_id}
                      rosterName={detail.assignee_roster_name}
                      rosterBio={detail.assignee_roster_bio}
                      rosterCredentials={detail.assignee_roster_credentials}
                      rosterPreferredName={detail.assignee_roster_preferred_name}
                      rosterEmail={detail.assignee_roster_email}
                      rosterPhone={detail.assignee_roster_phone}
                    />
                  </Field>
                ) : detail.kind === 'intake'
                  && (currentStageKey === READY_FOR_ASSIGNMENT_STAGE_KEY || currentStageKey === ASSIGNED_STAGE_KEY) ? (
                  <Field label="Care manager">
                    <span className="inline-flex items-center gap-2" data-testid="drawer-cm-unassigned">
                      <span aria-hidden="true" className={avatarClasses(false)}>?</span>
                      <span className="text-ink-soft">Unassigned</span>
                      {/* NEVER bare "Assign" — that label is reserved against the
                          desk-owner control collision (MoveStageDialog rule). */}
                      {isAssigned && canManage && !detail.closed_at && (
                        <TextAction data-testid="drawer-cm-assign" onClick={startAssign}>
                          Assign care manager
                        </TextAction>
                      )}
                      {/* LP-3: a SPICE therapist beside the care manager on Ready for
                          Assignment (the bar below carries "Assign care manager"). */}
                      {currentStageKey === READY_FOR_ASSIGNMENT_STAGE_KEY && canManage && !detail.closed_at && !detail.spice_therapist_roster_id && (
                        <TextAction data-testid="drawer-spice-assign" onClick={startAssignSpice}>
                          Assign SPICE therapist
                        </TextAction>
                      )}
                    </span>
                  </Field>
                ) : null}
                {/* LP-3: the client's SPICE therapist, when the card's client has one. */}
                {detail.kind === 'intake' && detail.spice_therapist_roster_id && (
                  <Field label="SPICE therapist">
                    <span data-testid="drawer-spice-therapist" className="text-ink">{detail.spice_therapist_name ?? 'Assigned'}</span>
                  </Field>
                )}
                {/* Task drawer leads with Due Date (the actionable field); intakes get
                    it in their stage order below. */}
                {detail.kind === 'task' && dueDateField}
                {detail.kind === 'task' && nextActionField}
                {/* LP-2: the client the task is about. The client bubble (LP-43), a link to the record;
                    whoever may edit the task links, changes or clears it. */}
                {detail.kind === 'task' && (detail.client_id || canEdit) && (
                  <Field label="Client">
                    {detail.client_id && detail.client_display_name ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/t/${tenantSlug}/clients/${detail.client_id}`}
                          data-testid="drawer-task-client"
                          className={`max-w-full truncate text-sm ${CLIENT_BUBBLE_CLASS}`}
                        >
                          {detail.client_display_name}
                        </Link>
                        {canEdit && (
                          <TextAction data-testid="drawer-task-client-clear" onClick={() => editField({ client_id: null })}>
                            Remove
                          </TextAction>
                        )}
                      </span>
                    ) : (
                      <ClientLookupField
                        tenantSlug={tenantSlug}
                        canLookup={canViewAnyClientRoster(whoami)}
                        onPick={(c) => editField({ client_id: c.id })}
                        inputId="drawer-task-client"
                        testIdPrefix="drawer-task-client"
                        label="Link a client"
                        className="relative"
                      />
                    )}
                  </Field>
                )}
                {/* WS-7: the task's category — vocabulary picker (managers) or the
                    resolved label. Task-only; the server rejects it on an intake. */}
                {/* LP-11: a seat that only manages its own tasks (a care
                    manager or SPICE seat) sees no Category at all. */}
                {detail.kind === 'task' && !ownTasksOnly && (
                  canEdit ? (
                    <Field label="Category">
                      <SelectField size="compact"
                        value={detail.category ?? ''}
                        aria-label="Category"
                        data-testid="drawer-category-select"
                        onChange={(e) => editField({ category: e.target.value || null })}
                      >
                        <option value="">None</option>
                        {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        {/* An off-vocabulary stored value (a since-edited config) stays selectable. */}
                        {detail.category && !categories.some((c) => c.value === detail.category) && (
                          <option value={detail.category}>{detail.category}</option>
                        )}
                      </SelectField>
                    </Field>
                  ) : (
                    detail.category && <Field label="Category">{taskCategoryLabel(categories, detail.category)}</Field>
                  )
                )}
                {/* PR3: the process_item generic stage control — advance OR move back
                    via a picker of the card's OWN board's active stages (the intake
                    advance flow + Assign dialogs never fire for this kind). The server
                    enforces the forward gates; a refusal names the unfinished item(s). */}
                {detail.kind === 'process_item' && !detail.closed_at && (
                  <Field label="Stage">
                    {canManage ? (
                      <SelectField size="compact"
                        value={detail.stage_id ?? ''}
                        aria-label="Stage"
                        data-testid="drawer-process-stage-select"
                        disabled={moveStage.isPending}
                        onChange={(e) => {
                          const next = stages.find((s) => s.id === e.target.value);
                          if (!next || next.id === detail.stage_id) return;
                          setProcessMoveError(null);
                          moveStage.mutate(
                            { id: detail.id, input: { to_stage: next.key } },
                            { onError: (err) => setProcessMoveError(moveBlockedMessage(err, contact)) },
                          );
                        }}
                      >
                        {[...stages].sort((a, b) => a.sort_order - b.sort_order).map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </SelectField>
                    ) : (
                      stageLabel ?? '–'
                    )}
                    {processMoveError && (
                      <p role="alert" data-testid="drawer-process-move-error" className="text-xs text-pepper-deep mt-1">{processMoveError}</p>
                    )}
                  </Field>
                )}
                {processFolderUrl && (
                  <Field label={processFolderLabel}>
                    <a
                      href={processFolderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="drawer-folder-link"
                      className="inline-flex items-center gap-1.5 rounded-pill border border-sage-200 bg-paper px-3 py-1 text-[0.78125rem] font-semibold text-sage-800 no-underline shadow-sh1 hover:bg-sage-50 hover:shadow-sh2 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                    >
                      Open {processFolderLabel}
                      <ArrowUpRight size={13} aria-hidden="true" className="text-sage-400 flex-shrink-0" />
                    </a>
                  </Field>
                )}
                {/* Field pack v1: the board's configured category field (config-
                    labeled, e.g. "Role") on a process card. Managers pick from the
                    config options (a stored off-list value stays selectable — the
                    task-category pattern); others read the value. A value with no
                    surviving config shows read-only under a neutral label. */}
                {detail.kind === 'process_item' && (cardFields.category || detail.category) && (
                  <Field label={cardFields.category?.label ?? 'Category'}>
                    {canManage && cardFields.category && cardFields.category.multi ? (
                      // Several values per card (a candidate for two roles): a
                      // checkbox group over the config options plus any stored
                      // value no longer on the list (a legacy label stays
                      // ticked, never silently dropped). Writes the full set.
                      <div
                        role="group"
                        aria-label={cardFields.category.label}
                        data-testid="drawer-process-category-group"
                        className="flex flex-col gap-1"
                      >
                        {[...new Set([...cardFields.category.options, ...processCategoryValues])].map((opt) => {
                          const checked = processCategoryValues.includes(opt);
                          return (
                            <label key={opt} className="inline-flex items-center gap-2 text-[0.8125rem] text-ink">
                              <input
                                type="checkbox"
                                className="accent-sage-800"
                                aria-label={opt}
                                checked={checked}
                                onChange={() => {
                                  const next = checked
                                    ? processCategoryValues.filter((v) => v !== opt)
                                    : [...processCategoryValues, opt];
                                  editField({ categories: next.length > 0 ? next : null });
                                }}
                              />
                              {opt}
                            </label>
                          );
                        })}
                      </div>
                    ) : canManage && cardFields.category ? (
                      <SelectField size="compact"
                        value={detail.category ?? ''}
                        aria-label={cardFields.category.label}
                        data-testid="drawer-process-category-select"
                        onChange={(e) => editField({ category: e.target.value || null })}
                      >
                        <option value="">None</option>
                        {cardFields.category.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                        {detail.category && !cardFields.category.options.includes(detail.category) && (
                          <option value={detail.category}>{detail.category}</option>
                        )}
                      </SelectField>
                    ) : (
                      <span data-testid="drawer-process-category">{processCategoryValues.length > 0 ? processCategoryValues.join(' · ') : '–'}</span>
                    )}
                  </Field>
                )}
                {/* Polish fix #2: the PROCESS owner (assignee_user) + a "Hand off…"
                    control that opens the hand-off picker (Block C's HandoffDialog).
                    Distinct from "Assign care manager" below (assignee_roster, the
                    clinical owner — two-assigns rule). Founder soak: labelled "Owner"
                    (never "Assigned to") so intake ownership can't read as CM
                    assignment. The text action and the avatar both open the picker
                    for a manage_work_items actor, matching the board card's avatar. */}
                <Field label="Owner">
                  <Assignee
                    name={detail.assignee_user_name}
                    size="sm"
                    testId="drawer-owner-name"
                    onAvatarClick={canManage ? () => setHandoffOpen(true) : undefined}
                    avatarTitle="Hand off to someone else"
                    avatarAriaLabel="Hand off to someone else"
                    avatarTestId="drawer-owner-avatar"
                  />
                  {canManage && (
                    <TextAction data-testid="drawer-assign-owner" onClick={() => setHandoffOpen(true)} className="ml-1.5">
                      {detail.assignee_user_name ? 'Hand off…' : 'Assign owner…'}
                    </TextAction>
                  )}
                </Field>
                {/* Editable recurrence (v3.x): a recurring task shows its cadence + Edit
                    (→ updateRecurringSeries re-derives next_due + the live due_date); a
                    one-off task gets "+ Add a repeat" (→ attachRecurrenceToTask turns it
                    recurring in place). Both reuse the quick-add picker. Stop/Delete below. */}
                {detail.kind === 'task' && (
                  <Field label="Repeats">
                    <RecurrenceBlock detail={detail} canManage={canEdit} ruleQuery={ruleQuery} updateSeries={updateSeries} attachRecurrence={attachRecurrence} />
                  </Field>
                )}
                {/* Shared assignment (0108): the process-owner's collaborators. In the
                    process-owner area, deliberately separate from "Assign care manager"
                    below (the CM stays a single clinical owner — the two-assigns rule). */}
                <CollaboratorsField
                  tenantSlug={tenantSlug}
                  workItemId={detail.id}
                  ownerUserId={detail.assignee_user_id}
                  collaborators={detail.collaborators}
                  members={members}
                  canManage={canManage}
                />
                {/* Due Date for an intake sits here, in the stage-driven order (a task
                    renders it first, above "Owner"). "Opened" moved to the title
                    subheading. */}
                {detail.kind !== 'task' && dueDateField}
                {detail.kind !== 'task' && nextActionField}
                {(detail.contact_name || detail.contact_phone || detail.contact_relationship || detail.contact_is_poa) && (
                  <Field label="Contact">
                    <span data-testid="drawer-contact">
                      {[detail.contact_name, detail.contact_phone].filter(Boolean).join(' · ') || '–'}
                      {detail.contact_relationship ? ` · ${detail.contact_relationship}` : ''}
                      {detail.contact_is_poa && <span className="ml-1.5 text-[10px] text-warm-700 font-semibold uppercase bg-warm-100 rounded-pill px-1.5 py-0.5">POA</span>}
                    </span>
                  </Field>
                )}
                {detail.service_requested && <Field label="Service">{displayValue(detail.service_requested)}</Field>}
                {/* Slice 3b unit flare: color dot + name, mirroring the card/header (resolves unit_id → units.color, else palette slot). */}
                {detail.unit_name && (
                  <Field label="Unit">
                    <span className="inline-flex items-center gap-1.5" data-testid="drawer-unit-field">
                      <span aria-hidden="true" className="inline-block w-2 h-2 rounded-pill flex-shrink-0"
                        style={{ backgroundColor: unitDotColor(detail.unit_id, detail.unit_color) ?? 'var(--color-sage-300)' }} />
                      {detail.unit_name}
                    </span>
                  </Field>
                )}
                <LocationField workItemId={detail.id} kind={detail.kind} clientId={detail.client_id ?? null} />

                {/* Reversibility rides the summary card's foot — it changes the
                    assignment facts listed directly above it. */}
                <AssignReversibilityBlock
                  detail={detail}
                  canReassignCm={canReassignCm}
                  canUnassignCm={canUnassignCm}
                  canMoveBack={canMoveBack}
                  priorStages={priorStages}
                  stageLabelByKey={stageLabelByKey}
                  assignError={assignError}
                  onStartAssign={startAssign}
                  requestConfirm={setConfirmAction}
                  onBlockedError={(err) => setAssignError(moveBlockedMessage(err, contact))}
                  unassignCm={unassignCm}
                  moveStage={moveStage}
                />
                </DrawerRowList>
              </DrawerSection>

              {detail.warnings && detail.warnings.length > 0 && (
                <p className="text-xs text-pepper-deep" data-testid="drawer-warnings">{detail.warnings.join('; ')}</p>
              )}

              {/* Editable intake-tracker fields (capture-at-call form is
                  primary; this is the correction surface, collapsed by
                  default — see IntakeDetailsForm). */}
              {canManage && detail.kind === 'intake' && (
                <IntakeDetailsForm detail={detail} lists={lists} defaults={defaults} freeText={freeText} onEdit={editField} />
              )}

              <StageChecklist
                items={detail.checklist}
                stages={stages}
                currentStageKey={currentStageKey}
                canManage={canManage}
                busy={toggleItem.isPending || moveStage.isPending}
                onToggle={(itemId, done) => toggleItem.mutate({ id: detail.id, itemId, done })}
                onSetNote={(itemId, note) => setNote.mutate({ id: detail.id, itemId, note })}
                onSetLink={(itemId, linkUrl) => setLink.mutate({ id: detail.id, itemId, linkUrl })}
                onSetDoneDate={(itemId, doneAt) => setDoneDate.mutate({ id: detail.id, itemId, doneAt })}
                onSetValue={(itemId, value) => setValue.mutate({ id: detail.id, itemId, value })}
                caseOverdue={isOverdue(detail)}
                onAdvanceCurrentStage={
                  showAdvance ? handleAdvance
                    : showTerminalComplete ? handleTerminalComplete
                      : canAssignCm ? startAssign
                        : showProcessAdvance ? handleProcessAdvance
                          : null
                }
                advanceOverride={canAssignCm ? { label: 'Assign care manager', testId: 'drawer-assign-cm' } : null}
                stageIntro={readyForAssignmentIntro}
                firstItemAddon={suggestNode ? { stageKey: READY_FOR_ASSIGNMENT_STAGE_KEY, node: suggestNode } : null}
                heading={detail.kind === 'intake' ? 'Intake checklist' : 'Checklist'}
                tenantSlug={tenantSlug}
              />
              {(advanceError ?? processAdvanceError) && (
                <p role="alert" data-testid="drawer-advance-error" className="text-xs text-pepper-deep">{advanceError ?? processAdvanceError}</p>
              )}

              {/* WS-7: free-text working notes — the founder's "free text, not a
                  changelog" home. Task-only; persists on blur (the house
                  text-field pattern). The activity/comment stream (which serves
                  collaboration) stays, BELOW this. */}
              {detail.kind === 'task' && (canEdit || detail.notes) && (
                <DrawerSection title="Notes" testId="drawer-notes">
                  {canEdit ? (
                    <textarea
                      key={detail.id}
                      defaultValue={detail.notes ?? ''}
                      aria-label="Notes"
                      data-testid="drawer-notes-input"
                      rows={4}
                      placeholder="Anything worth keeping with this task…"
                      onBlur={(e) => e.target.value !== (detail.notes ?? '') && editField({ notes: e.target.value || null })}
                      className="w-full rounded-base border border-sage-200 bg-paper px-3 py-2 text-[13px] text-ink leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                    />
                  ) : (
                    <p className="text-[13px] text-ink whitespace-pre-wrap" data-testid="drawer-notes-readonly">{detail.notes}</p>
                  )}
                </DrawerSection>
              )}

              {/* Card attachments (0162): working files sit WITH the card's
                  working material, and the activity stream stays last. The
                  section renders nothing at all on a board whose process has
                  attachments_enabled off (the intake board, by seeded
                  default) — the fence is fail-closed here and re-checked
                  server-side on every write. */}
              <AttachmentsBlock
                tenantSlug={tenantSlug}
                workItemId={detail.id}
                attachmentsEnabled={detail.attachments_enabled}
                canManage={canManage}
                clientId={detail.client_id}
              />

              {/* The intake board's Documents section. Intake cards get the
                  CLIENT's real documents (client_documents, 0131) rather than
                  card attachments — the intake attachment fence above stays
                  down, and "documents show up on the client record's Documents
                  tab" is true by construction because these ARE those rows.
                  Intake-only: a task has no client, and a process board (talent
                  / resources) keeps card attachments, which is the right
                  register for a resume or a brochure. */}
              {detail.kind === 'intake' && (
                <ClientDocumentsBlock
                  tenantSlug={tenantSlug}
                  clientId={detail.client_id}
                  canReadDocuments={canReadDocuments}
                  canFileDocuments={canFileDocuments}
                  folderLinkUrl={folderLinkUrl}
                />
              )}

              <ActivityBlock
                detail={detail}
                canManage={canManage}
                addComment={addComment}
                stageLabelByKey={stageLabelByKey}
                currentUserId={whoami?.user_id ?? null}
                canModerateNotes={canModerateWorkItemNotes(whoami)}
                editEvent={editEvent}
                deleteEvent={deleteEvent}
                setEnteredOn={setEnteredOn}
              />
            </>
          )}
        </div>
      </Drawer>

      {assigning && detail && (
        <MoveStageDialog
          mode="assign"
          toStage={ASSIGNED_STAGE_KEY}
          unitId={detail.unit_id}
          prospectZip={detail.prospect_zip}
          clientId={detail.client_id}
          initialRosterId={assignInitialRosterId}
          offerSpice={!isAssigned}
          spiceFirst={assignSpiceFirst}
          submitting={moveStage.isPending || reassignCm.isPending}
          onCancel={() => setAssigning(false)}
          onConfirm={(payload) => {
            // Brief 3 PR3: on an already-Assigned case this dialog ASSIGNS or
            // REASSIGNS in place (a same-stage moveStage would no-op the swap via
            // the no-op guard; soak r2 widened reassignCm to accept a CM-less
            // Assigned case); on a Ready-for-Assignment case it's the initial
            // assign (ready → assigned).
            if (isAssigned) {
              if (!payload.assignee_roster_id) { setAssignError('Pick a care manager.'); return; }
              reassignCm.mutate(
                { id: detail.id, rosterId: payload.assignee_roster_id },
                {
                  onSuccess: () => { setAssigning(false); setAssignError(null); },
                  onError: (err) => { setAssigning(false); setAssignError(moveBlockedMessage(err, contact)); },
                },
              );
              return;
            }
            moveStage.mutate(
              { id: detail.id, input: { to_stage: ASSIGNED_STAGE_KEY, ...payload } },
              {
                onSuccess: () => { setAssigning(false); setAssignError(null); },
                onError: (err) => { setAssigning(false); setAssignError(moveBlockedMessage(err, contact)); }, // names the unfinished item(s)
              },
            );
          }}
        />
      )}

      {advanceSetupTo !== null && detail && (
        <MoveStageDialog
          mode="client-setup"
          toStage={advanceSetupTo}
          workItemId={detail.id}
          prospectZip={detail.prospect_zip ?? null}
          defaultFirstName={splitProspectName(detail.prospect_name).first}
          defaultLastName={splitProspectName(detail.prospect_name).last}
          submitting={moveStage.isPending}
          onCancel={() => setAdvanceSetupTo(null)}
          onConfirm={(payload) => {
            moveStage.mutate(
              { id: detail.id, input: { to_stage: advanceSetupTo, auto_complete_stage: true, ...payload } },
              {
                onSuccess: () => { setAdvanceSetupTo(null); setHandoffOpen(true); },
                // The refusal reads where every advance's does.
                onError: (err) => { setAdvanceSetupTo(null); setAdvanceError(moveBlockedMessage(err, contact)); },
              },
            );
          }}
        />
      )}

      {/* Slice 3b-2: styled soft-nag confirm (replaces window.confirm). */}
      {confirmAdvance && (
        <ConfirmDialog
          title={confirmAdvance.toStage === null ? 'Mark completed?' : 'Advance anyway?'}
          body={confirmAdvance.body}
          confirmLabel={confirmAdvance.toStage === null ? 'Complete' : 'Advance'}
          onCancel={() => setConfirmAdvance(null)}
          onConfirm={() => { const to = confirmAdvance.toStage; setConfirmAdvance(null); if (to) doAdvance(to); else doComplete(); }}
        />
      )}

      {/* Brief 3 PR3: the reversibility confirm (move-back / unassign); 0155
          reuses it for the intake-delete + archive/restore-client confirms. */}
      {confirmAction && (
        <ConfirmDialog
          title={confirmAction.title}
          body={confirmAction.body}
          confirmLabel={confirmAction.confirmLabel}
          destructive={confirmAction.destructive}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => { const run = confirmAction.run; setConfirmAction(null); run(); }}
        />
      )}

      {detail && taskAction === 'delete' && (
        <ConfirmDialog
          title="Delete this task?"
          body="This can’t be undone."
          confirmLabel="Delete"
          destructive
          onCancel={() => setTaskAction(null)}
          onConfirm={() => deleteTask.mutate(detail.id, { onSuccess: () => { setTaskAction(null); onClose(); } })}
        />
      )}
      {detail && taskAction === 'stop' && detail.recurrence_id && (
        <ConfirmDialog
          title="Stop repeating?"
          body="The current task stays on your list; it just won’t come back."
          confirmLabel="Stop repeating"
          onCancel={() => setTaskAction(null)}
          onConfirm={() => pauseSeries.mutate(detail.recurrence_id!, { onSuccess: () => setTaskAction(null) })}
        />
      )}

      {/* Block C: hand off to the next process owner after a successful advance
          (also opened from the Owner row's "Hand off…" action and avatar). */}
      {handoffOpen && detail && (
        <HandoffDialog
          members={members}
          currentUserId={detail.assignee_user_id}
          currentUserName={detail.assignee_user_name ?? null}
          submitting={patchItem.isPending}
          onSkip={() => setHandoffOpen(false)} // keep the current owner (never strands)
          onHandoff={(uid) => {
            // A changed pick writes through, INCLUDING "Unassigned" (null):
            // clearing the desk owner is a real choice, the same one the
            // board's avatar picker already honours. Same pick = no write.
            if (uid !== detail.assignee_user_id) editField({ assignee_user_id: uid });
            setHandoffOpen(false);
          }}
        />
      )}
    </>
  );
}
